import { useState, useEffect, useRef } from "react";
import type { AppFilterStore, AppFilterContextValue, PageFilterState, PageFilterRegistration } from "./types";
import { readFiltersFromUrl, writeFiltersToUrl } from "./urlSync";
export function useFilterStore(): AppFilterContextValue {
  const [store, setStore] = useState<AppFilterStore>({});
  const registeredPages = useRef<Set<string>>(new Set());
  const sessionKeys = useRef<Record<string, string>>({});
  const pageFilterKeys = useRef<Record<string, string[]>>({});
  const urlOptOut = useRef<Set<string>>(new Set());
  const registerPage = (registration: PageFilterRegistration) => {
    const {
      pageKey,
      sessionStorageKey,
      filters,
      syncToUrl
    } = registration;
    if (syncToUrl === false) urlOptOut.current.add(pageKey);

    // Multiple modules can share a pageKey (e.g. all property tabs) — each
    // contributes its own filter keys. The first registration wins on the
    // session storage key; subsequent calls only add filters not yet known.
    const existingKeys = pageFilterKeys.current[pageKey] ?? [];
    const newKeys = Object.keys(filters).filter(k => !existingKeys.includes(k));
    if (!registeredPages.current.has(pageKey)) {
      registeredPages.current.add(pageKey);
      sessionKeys.current[pageKey] = sessionStorageKey;
    }
    pageFilterKeys.current[pageKey] = [...existingKeys, ...newKeys];
    if (newKeys.length === 0) return;

    // Build defaults for the new filter keys only.
    const defaults: PageFilterState = {};
    for (const filterKey of newKeys) {
      defaults[filterKey] = {
        values: null,
        operator: filters[filterKey].defaultOperator
      };
    }

    // Layer 1: URL takes precedence if any filter param is present.
    const urlFilters = urlOptOut.current.has(pageKey) ? {} : readFiltersFromUrl(newKeys);
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    if (hasUrlFilters) {
      for (const [filterKey_0, fromUrl] of Object.entries(urlFilters)) {
        if (defaults[filterKey_0] && fromUrl) {
          defaults[filterKey_0] = {
            values: fromUrl.values,
            operator: fromUrl.operator
          };
        }
      }
    } else if (typeof window !== "undefined") {
      const activeSessionKey = sessionKeys.current[pageKey];
      const stored = activeSessionKey ? sessionStorage.getItem(activeSessionKey) : null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const isNewFormat = newKeys.some(key => parsed[key] && typeof parsed[key] === "object" && "operator" in parsed[key]);
          if (isNewFormat) {
            for (const filterKey_1 of newKeys) {
              if (parsed[filterKey_1]) {
                defaults[filterKey_1] = {
                  values: parsed[filterKey_1].values ?? null,
                  operator: parsed[filterKey_1].operator ?? defaults[filterKey_1].operator
                };
              }
            }
          } else {
            // Legacy session format: "statusFilter" → values, "statusOp" → operator.
            for (const filterKey_2 of newKeys) {
              const oldValuesKey = `${filterKey_2}Filter`;
              const oldOpKey = `${filterKey_2}Op`;
              if (oldValuesKey in parsed || oldOpKey in parsed) {
                defaults[filterKey_2] = {
                  values: parsed[oldValuesKey] ?? null,
                  operator: parsed[oldOpKey] ?? defaults[filterKey_2].operator
                };
              }
            }
          }
        } catch {
          // Fall through to defaults.
        }
      }
    }
    setStore(prev => ({
      ...prev,
      [pageKey]: {
        ...defaults,
        ...(prev[pageKey] ?? {})
      }
    }));
  };
  const toggleValue = (pageKey_0: string, filterKey_3: string, value: string) => {
    setStore(prev_0 => {
      const page = prev_0[pageKey_0];
      if (!page?.[filterKey_3]) return prev_0;
      const current = page[filterKey_3].values;
      let newValues: string[] | null;
      if (!current) {
        newValues = [value];
      } else if (current.includes(value)) {
        const filtered = current.filter(v => v !== value);
        newValues = filtered.length > 0 ? filtered : null;
      } else {
        newValues = [...current, value];
      }
      return {
        ...prev_0,
        [pageKey_0]: {
          ...page,
          [filterKey_3]: {
            ...page[filterKey_3],
            values: newValues
          }
        }
      };
    });
  };
  const setOperator = (pageKey_1: string, filterKey_4: string, operator: string) => {
    setStore(prev_1 => {
      const page_0 = prev_1[pageKey_1];
      if (!page_0?.[filterKey_4]) return prev_1;
      return {
        ...prev_1,
        [pageKey_1]: {
          ...page_0,
          [filterKey_4]: {
            ...page_0[filterKey_4],
            operator
          }
        }
      };
    });
  };

  // Bulk-apply a view's filters: known keys present in `next` take its
  // values/operator; any other registered key is cleared so the bar reflects
  // exactly the view. No-op if the page hasn't registered yet.
  const setPageFilters = (pageKey_2: string, next: PageFilterState) => {
    setStore(prev_2 => {
      const page_1 = prev_2[pageKey_2];
      if (!page_1) return prev_2;
      const merged: PageFilterState = {};
      for (const [filterKey_5, state] of Object.entries(page_1)) {
        const incoming = next[filterKey_5];
        merged[filterKey_5] = incoming ? {
          values: incoming.values ?? null,
          operator: incoming.operator ?? state.operator
        } : {
          ...state,
          values: null
        };
      }
      return {
        ...prev_2,
        [pageKey_2]: merged
      };
    });
  };
  const clearFilter = (pageKey_3: string, filterKey_6: string) => {
    setStore(prev_3 => {
      const page_2 = prev_3[pageKey_3];
      if (!page_2?.[filterKey_6]) return prev_3;
      return {
        ...prev_3,
        [pageKey_3]: {
          ...page_2,
          [filterKey_6]: {
            ...page_2[filterKey_6],
            values: null
          }
        }
      };
    });
  };
  const clearAllFilters = (pageKey_4: string) => {
    setStore(prev_4 => {
      const page_3 = prev_4[pageKey_4];
      if (!page_3) return prev_4;
      const cleared: PageFilterState = {};
      for (const [filterKey_7, filterState] of Object.entries(page_3)) {
        cleared[filterKey_7] = {
          ...filterState,
          values: null
        };
      }
      return {
        ...prev_4,
        [pageKey_4]: cleared
      };
    });
  };

  // Persist to sessionStorage and URL on every store change.
  useEffect(() => {
    for (const [pageKey_5, pageFilters] of Object.entries(store)) {
      const sessionKey = sessionKeys.current[pageKey_5];
      if (sessionKey) {
        sessionStorage.setItem(sessionKey, JSON.stringify(pageFilters));
      }
      const filterKeys = pageFilterKeys.current[pageKey_5];
      if (filterKeys && !urlOptOut.current.has(pageKey_5)) {
        writeFiltersToUrl(filterKeys, pageFilters);
      }
    }
  }, [store]);
  return {
    store,
    registerPage,
    toggleValue,
    setOperator,
    setPageFilters,
    clearFilter,
    clearAllFilters
  };
}